import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { api, ApiError } from '../api/client'
import type { BootstrapResponse, RuntimeNavigation, RuntimeSession } from '../api/types'
import { initSdk, rememberRuntimeKey, RUNTIME_OPTION, callBitrix, sdkAuth } from '../lib/sdk'
import { loadManifest, loadStaticForm, type StaticManifest } from '../lib/static-runtime'

const STORAGE_KEY = 'sas_bitrix_runtime_token'
export const useRuntimeStore = defineStore('runtime', () => {
  const token = ref<string | null>(sessionStorage.getItem(STORAGE_KEY))
  const session = ref<RuntimeSession | null>(null)
  const navigation = ref<RuntimeNavigation>({ folders: [], publications: [] })
  const bootstrapState = ref<BootstrapResponse | null>(null)
  const blocked = ref(false)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const staticMode = ref(false)
  const runtimeKey = ref('')
  const manifest = ref<StaticManifest | null>(null)
  let manifestExpires = 0
  const ready = computed(() => Boolean(session.value && (staticMode.value || token.value)))
  const currentUser = computed(() => session.value?.context?.bitrix?.current_user ?? null)
  const isAdmin = computed(() => Boolean(currentUser.value?.is_admin))
  function setToken(value: string | null) {
    token.value = value
    if (value) sessionStorage.setItem(STORAGE_KEY, value)
    else sessionStorage.removeItem(STORAGE_KEY)
  }
  async function tryStatic(key?: string) {
    await initSdk()
    const auth = await sdkAuth()
    runtimeKey.value = key || String(window.BX24!.appOption.get(RUNTIME_OPTION) || '')
    if (!runtimeKey.value) return false
    const cached = await loadManifest(runtimeKey.value, auth.member_id)
    manifest.value = cached.data; manifestExpires = cached.expires
    if (cached.data.mode === 'server') { staticMode.value = false; return false }
    staticMode.value = true
    session.value = { id: '', workspace_id: cached.data.workspace_id, entry_integration_id: null, publication_id: null,
      adapter_type: 'bitrix', external_user_id: null, expires_at: new Date(cached.expires).toISOString(),
      context: { surface: 'bitrix', bitrix: { member_id: auth.member_id, domain: auth.domain, current_user: { is_admin: Boolean(window.BX24!.isAdmin?.()) } } } }
    blocked.value = cached.data.mode === 'blocked'
    error.value = blocked.value ? 'Доступ к приложению ограничен. Проверьте подключение и статус пространства.' : null
    navigation.value = { folders: blocked.value ? [] : cached.data.folders || [], publications: blocked.value ? [] : cached.data.forms }
    // Identity is supplied by the portal, never by form input. No request to S-A-S.
    if (!blocked.value) {
      const user = await callBitrix('user.current', {})
      const bitrix = session.value.context.bitrix!
      bitrix.current_user_id = String(user.ID)
      bitrix.current_user = { id: String(user.ID), name: user.NAME, last_name: user.LAST_NAME, departments: user.UF_DEPARTMENT || [], is_admin: Boolean(window.BX24!.isAdmin?.()) }
      session.value.external_user_id = String(user.ID)
    }
    return true
  }
  async function exchange(ticket: string) {
    loading.value = true; error.value = null; blocked.value = false
    try {
      const result = await api.bootstrap(ticket)
      bootstrapState.value = result
      if (result.status === 'ready' && result.runtime_token) {
        setToken(result.runtime_token)
        const key = String(result.payload?.runtime_key || '')
        if (key) await rememberRuntimeKey(key)
        if (key && await tryStatic(key)) return result
        await loadSession(); await loadNavigation()
      }
      return result
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) deny(e)
      error.value = e instanceof Error ? e.message : 'Ошибка bootstrap'; throw e
    } finally { loading.value = false }
  }
  async function loadSession() {
    if (!token.value) throw new Error('Runtime token отсутствует')
    session.value = await api.session(token.value)
  }
  async function loadNavigation() {
    if (staticMode.value) {
      if (Date.now() >= manifestExpires) await tryStatic(runtimeKey.value)
      return
    }
    if (!token.value || blocked.value) return
    const currentToken = token.value
    const next = await api.navigation(currentToken)
    if (!blocked.value && token.value === currentToken) navigation.value = next
  }
  async function restore() {
    try {
      if (await tryStatic()) return true
      if (!token.value) return false
      await loadSession(); await loadNavigation(); return true
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) { deny(e); return true }
      error.value = e instanceof Error ? e.message : 'Ошибка сессии'
      setToken(null); session.value = null
      if (e instanceof ApiError && e.status === 401) return false
      throw e
    }
  }
  async function form(publicKey: string) {
    if (staticMode.value) {
      await ensureStaticAccess(publicKey)
      return loadStaticForm(runtimeKey.value, manifest.value!, publicKey)
    }
    if (!token.value) throw new Error('Сессия отсутствует')
    return api.form(token.value, publicKey)
  }
  async function refreshManifest() {
    if (!runtimeKey.value) return false
    const auth = await sdkAuth()
    const previous = manifest.value?.generation
    const cached = await loadManifest(runtimeKey.value, auth.member_id, true)
    manifest.value = cached.data; manifestExpires = cached.expires
    blocked.value = cached.data.mode === 'blocked'
    navigation.value = { folders: blocked.value ? [] : cached.data.folders || [], publications: blocked.value ? [] : cached.data.forms }
    return previous !== cached.data.generation
  }
  let serverRequest: Promise<string> | undefined
  async function ensureServerSession(force = false): Promise<string> {
    if (serverRequest) return serverRequest
    if (token.value && !force) return token.value
    serverRequest = (async () => {
      async function create(auth: Awaited<ReturnType<typeof sdkAuth>>) {
        return api.bitrixSession({ runtime_key: runtimeKey.value, member_id: auth.member_id,
          access_token: auth.access_token, auth_expires: Math.max(60, Math.min(3600, Math.floor(((auth.expires_in || Date.now() + 300000) - Date.now()) / 1000))) })
      }
      let response
      try { response = await create(await sdkAuth(force)) }
      catch (e) {
        if (!(e instanceof ApiError) || e.status !== 401 || force) throw e
        response = await create(await sdkAuth(true))
      }
      setToken(response.runtime_token)
      return response.runtime_token
    })()
    try { return await serverRequest } finally { serverRequest = undefined }
  }
  async function serverCall<T>(operation: (token: string) => Promise<T>): Promise<T> {
    const attempted = await ensureServerSession()
    try { return await operation(attempted) }
    catch (e) {
      if (!(e instanceof ApiError) || e.status !== 401) throw e
      const refreshed = token.value && token.value !== attempted ? token.value : await ensureServerSession(true)
      return operation(refreshed)
    }
  }
  async function ensureStaticAccess(publicKey: string) {
    if (!staticMode.value) return
    if (Date.now() >= manifestExpires) {
      const oldGeneration = manifest.value?.generation
      await tryStatic(runtimeKey.value)
      if (!staticMode.value) throw new Error('Тариф изменился. Откройте приложение заново.')
      if (manifest.value?.generation !== oldGeneration) throw new Error('Опубликованная версия изменилась. Откройте форму заново перед отправкой.')
    }
    if (blocked.value || !manifest.value?.forms.some(item => item.public_key === publicKey)) throw new Error('Форма недоступна на текущем тарифе')
  }
  function deny(e: Error) { blocked.value = true; error.value = e.message; navigation.value = { folders: [], publications: [] } }
  return { blocked, deny, token, session, navigation, bootstrapState, loading, error, ready, currentUser, isAdmin,
    exchange, loadSession, loadNavigation, restore, staticMode, form, ensureStaticAccess, ensureServerSession, serverCall, refreshManifest }
})
